import { FeatureFlag } from '@types';
import useSWR from 'swr';
import { useMemo } from 'react';

// Feature flags are non-critical: use a plain fetch that does NOT try to
// refresh the token, rewrite localStorage, or redirect on auth errors.
// If the request fails, it simply fails and SWR keeps the last value.
// The access token rides in the httpOnly cookie (sent automatically on
// same-origin requests); no Authorization header.
async function featureFlagsFetcher<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(`Error while fetching feature flags: ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export const useFetchFeatureFlags = (
  filterByUser?: boolean,
  filterByRole?: boolean,
  userId?: number,
  userRole?: string
) => {
  const params = useMemo(() => {
    const queryParams = [];

    if (filterByUser && userId) {
      queryParams.push(`users_allowed=${userId}`);
    }

    if (filterByRole && userRole) {
      queryParams.push(`roles_allowed=${userRole}`);
    }

    return queryParams.length > 0 ? `${queryParams.join('&')}` : '';
  }, [filterByUser, filterByRole, userId, userRole]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data, error, mutate } = useSWR<FeatureFlag[]>(
    `/api/featureFlags${params ? `?${params}` : ''}`,
    featureFlagsFetcher,
    { refreshInterval: 0, shouldRetryOnError: false }
  );

  const refetch = () => {
    // eslint-disable-next-line  @typescript-eslint/no-floating-promises
    mutate();
  };

  return {
    featureFlags: data || [],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    error,
    loading: !data && !error,
    refetch,
  };
};
